import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";
import {
  useGetNewBarcodeMutation,
  useGetItemCreateDropdownValMutation,
  useGetOrderingProductsMutation,
  useGetBarcodeDetailsMutation,
} from "../../../redux/inventory/inventoryApiSlice";
import { getErrorMessage, dateFormatter } from "../../../utils/commonUtils";
import moment from "moment";

export interface CreateBarcodeFormValues {
  barcode: string;
  rxName: string;
  rxGroup: string;
  ndcCode: string;
  manufacturer: string;
  lotNumber: string;
  expirationDate: string;
  qty: string;
  inventoryUnit: string;
  itemType: string;
  source: string;
  orderingProductMasterId: string;
  refInfo: string;
  notes: string;
}

export interface CreateBarcodeFormPrefillData {
  ndcCode?: string;
  manufacturer?: string;
  lotNumber?: string;
  expirationDate?: string;
  qty?: string;
  rxName?: string;
  orderingProductMasterId?: string;
  inventoryUnitName?: string;
  source?: string;
  refInfo?: string;
}

interface CreateBarcodeFormProps {
  onSave: (values: CreateBarcodeFormValues) => void;
  onCancel: () => void;
  prefillData?: CreateBarcodeFormPrefillData;
}

const validationSchema = Yup.object({
  barcode: Yup.string().required("Barcode is required"),
  rxName: Yup.string().required("Rx Name is required"),
  rxGroup: Yup.string().required("Rx Group is required"),
  ndcCode: Yup.string().required("NDC Code is required"),
  manufacturer: Yup.string().required("Manufacturer is required"),
  lotNumber: Yup.string().required("Lot Number is required"),
  expirationDate: Yup.string().required("Expiration Date is required"),
  qty: Yup.string(),
  inventoryUnit: Yup.string().required("Inventory Unit is required"),
  itemType: Yup.string().required("Item Type is required"),
  source: Yup.string().required("Source is required"),
  orderingProductMasterId: Yup.string(),
  refInfo: Yup.string(),
  notes: Yup.string(),
});

const initialValues: CreateBarcodeFormValues = {
  barcode: "",
  rxName: "",
  rxGroup: "",
  ndcCode: "",
  manufacturer: "",
  lotNumber: "",
  expirationDate: "",
  qty: "",
  inventoryUnit: "",
  itemType: "",
  source: "",
  orderingProductMasterId: "",
  refInfo: "",
  notes: "",
};

const CreateBarcodeForm: React.FC<CreateBarcodeFormProps> = ({
  onSave,
  onCancel,
  prefillData,
}) => {
  const [getNewBarcode, { isLoading: getNewBarcodeLoading }] =
    useGetNewBarcodeMutation();
  const [getDropdownVal] = useGetItemCreateDropdownValMutation();
  const [getBarcodeDetails, { isLoading: barcodeDecodeLoading }] =
    useGetBarcodeDetailsMutation();

  const [getOrderingProducts] = useGetOrderingProductsMutation();

  const [units, setUnits] = useState<any[]>([]);
  const [rxTypes, setRxTypes] = useState<any[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);
  const [orderingProdOptions, setOrderingProdOptions] = useState<any[]>([]);
  const [decodeError, setDecodeError] = useState<{ message: string; key: number } | null>(null);

  const prefillInitialValues: CreateBarcodeFormValues = {
    ...initialValues,
    ndcCode: prefillData?.ndcCode || "",
    manufacturer: prefillData?.manufacturer || "",
    lotNumber: prefillData?.lotNumber || "",
    expirationDate: prefillData?.expirationDate || "",
    qty: prefillData?.qty || "",
    rxName: prefillData?.rxName || "",
    orderingProductMasterId: prefillData?.orderingProductMasterId || "",
    source: prefillData?.source || "",
    refInfo: prefillData?.refInfo || "",
  };

  const {
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
  } = useFormik({
    initialValues: prefillInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (
      values: CreateBarcodeFormValues,
      { setSubmitting }: FormikHelpers<CreateBarcodeFormValues>
    ) => {
      onSave(values);
      setSubmitting(false);
    },
  });

  const fetchNewBarcode = async () => {
    try {
      const response = await getNewBarcode({}).unwrap();
      if (response?.result) {
        setFieldValue("barcode", response.result);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBarcodeDecode = async (barcodeValue?: string) => {
    const barcode = barcodeValue || values.barcode;
    if (!barcode?.trim()) return;
    setDecodeError(null);
    try {
      const response: any = await getBarcodeDetails({
        useMfrBarcode: true,
        checkInternet: true,
        addNdcProductMaster: false,
        isMedicine: true,
        barcode: barcode,
      }).unwrap();
      if (response?.result) {
        const decoded = response.result;
        if (decoded.expirationDate &&
          moment(decoded.expirationDate, "MM/DD/YY", true).isValid()) {
          setFieldValue("expirationDate",
            dateFormatter(decoded.expirationDate) || "");
        }
        if (decoded.lotNumber) setFieldValue("lotNumber", decoded.lotNumber);
        if (decoded.ndcCode) setFieldValue("ndcCode", decoded.ndC_SKU);
        if (decoded.manufacturer) setFieldValue("manufacturer", decoded.manufacturer);
        if (decoded.group) setFieldValue("rxGroup", decoded.group);
        if (decoded.name) setFieldValue("rxName", decoded.name);
      }
    } catch (error) {
      setDecodeError({ message: getErrorMessage(error), key: Date.now() });
    }
  };

  const fetchDropdownValues = async () => {
    try {
      const response = await getDropdownVal({}).unwrap();
      if (response?.result) {
        const { units, rxTypes, stocks } = response.result;
        if (units) {
          setUnits(units);
          if (prefillData?.inventoryUnitName) {
            const matchedUnit = units.find(
              (u: any) =>
                u.unitName?.toLowerCase() === prefillData.inventoryUnitName!.toLowerCase()
            );
            if (matchedUnit) {
              setFieldValue("inventoryUnit", matchedUnit.id);
            }
          }
        }
        if (rxTypes) setRxTypes(rxTypes);
        if (stocks) setStocks(stocks);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchOrderingProducts = async () => {
    try {
      const response = await getOrderingProducts({
        includeDeleted: false,
        active: true,
      }).unwrap();
      if (response?.result) {
        setOrderingProdOptions([
          { value: "", label: "Select..." },
          ...response.result.map((option: any) => ({
            value: option.id,
            label: `${option.productNo} - ${option.productDescription} (${option.unit?.unitCode || ""})`,
          })),
        ]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchNewBarcode();
    fetchDropdownValues();
    fetchOrderingProducts();
  }, []);
  return (
    <div className="create-barcode-content">
      <Form onSubmit={handleSubmit}>
        {decodeError && (
          <div key={decodeError.key} className="text-danger text-center fw-bold mb-2">
            {decodeError.message}
          </div>
        )}
        <div className="barcode-form-grid">
          {/* Row 1 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Barcode: <span className="text-danger">*</span></label>
              <div className="input-with-icons">
                <Form.Control
                  size="sm"
                  name="barcode"
                  value={values.barcode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.barcode && !!errors.barcode}
                  disabled={getNewBarcodeLoading}
                  style={{ fontWeight: "bold", color: "#044d6e" }}
                />
                <i
                  className="bi bi-search icon-btn"
                  title="Decode barcode"
                  onClick={() => handleBarcodeDecode(values.barcode)}
                  style={{ cursor: barcodeDecodeLoading ? "wait" : "pointer" }}
                />
                <i
                  className="bi bi-arrow-clockwise icon-btn"
                  title="Refresh the barcode"
                  onClick={fetchNewBarcode}
                  style={{ cursor: "pointer" }}
                />
              </div>
              {touched.barcode && errors.barcode && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.barcode}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Rx Name: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="rxName"
                value={values.rxName}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.rxName && !!errors.rxName}
              />
              {touched.rxName && errors.rxName && (
                <Form.Control.Feedback type="invalid">
                  {errors.rxName}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Rx Group: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="rxGroup"
                value={values.rxGroup}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.rxGroup && !!errors.rxGroup}
              />
              {touched.rxGroup && errors.rxGroup && (
                <Form.Control.Feedback type="invalid">
                  {errors.rxGroup}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>NDC Code: <span className="text-danger">*</span></label>
              <div className="input-with-icons">
                <Form.Control
                  size="sm"
                  name="ndcCode"
                  value={values.ndcCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.ndcCode && !!errors.ndcCode}
                />
                <i className="bi bi-search icon-btn"
                  title="Decode barcode"
                  onClick={() => handleBarcodeDecode(values.ndcCode)}
                  style={{ cursor: barcodeDecodeLoading ? "wait" : "pointer" }} />
              </div>
              {touched.ndcCode && errors.ndcCode && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.ndcCode}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Manufacturer: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="manufacturer"
                value={values.manufacturer}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.manufacturer && !!errors.manufacturer}
              />
              {touched.manufacturer && errors.manufacturer && (
                <Form.Control.Feedback type="invalid">
                  {errors.manufacturer}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Lot Number: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="lotNumber"
                value={values.lotNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.lotNumber && !!errors.lotNumber}
              />
              {touched.lotNumber && errors.lotNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.lotNumber}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Expiration Date: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                type="date"
                name="expirationDate"
                value={values.expirationDate}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.expirationDate && !!errors.expirationDate}
              />
              {touched.expirationDate && errors.expirationDate && (
                <Form.Control.Feedback type="invalid">
                  {errors.expirationDate}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Qty:</label>
              <Form.Control
                size="sm"
                type="number"
                name="qty"
                value={values.qty}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Quantity"
              />
            </div>
            <div className="barcode-form-field">
              <label>Inventory Unit: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="inventoryUnit"
                value={values.inventoryUnit}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.inventoryUnit && !!errors.inventoryUnit}
                disabled={!!prefillData?.inventoryUnitName}
              >
                <option value="">Select</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unitName}
                  </option>
                ))}
              </Form.Select>
              {touched.inventoryUnit && errors.inventoryUnit && (
                <Form.Control.Feedback type="invalid">
                  {errors.inventoryUnit}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 4 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Item Type: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="itemType"
                value={values.itemType}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.itemType && !!errors.itemType}
              >
                <option value="">Select</option>
                {rxTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.rxTypeName}
                  </option>
                ))}
              </Form.Select>
              {touched.itemType && errors.itemType && (
                <Form.Control.Feedback type="invalid">
                  {errors.itemType}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Source: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="source"
                value={values.source}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.source && !!errors.source}
              >
                <option value="">Select</option>
                {stocks.map((stock) => (
                  <option key={stock.id} value={stock.id}>
                    {stock.stockName}
                  </option>
                ))}
              </Form.Select>
              {touched.source && errors.source && (
                <Form.Control.Feedback type="invalid">
                  {errors.source}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Ordering Product:</label>
              <Select
                classNamePrefix="react-select"
                options={orderingProdOptions}
                id="orderingProductMasterId"
                name="orderingProductMasterId"
                value={orderingProdOptions.find(
                  (option: any) => option.value === values.orderingProductMasterId
                )}
                onChange={(option: any) =>
                  setFieldValue("orderingProductMasterId", option?.value || "")
                }
                className="react-select-container"
                placeholder="Select"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Ref Info:</label>
              <Form.Control
                size="sm"
                name="refInfo"
                value={values.refInfo}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="barcode-form-field span-2">
              <label>Notes:</label>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                name="notes"
                value={values.notes}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </div>

        <div className="modal--action--btn--wrapper">
          <Button variant="primary" type="submit">
            Save
          </Button>
          <Button variant="outline-primary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateBarcodeForm;
